import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
  ModalBody,
  ModalFooter,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FormEvent, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import { BI_GROUPS_KEY } from '../../../../hooks/useBi';
import {
  BiGroup,
  BiReport,
  biErrorMessage,
  biService,
} from '../../../../services/biService';
import {
  BI_DEFAULT_COLOR,
  BI_DEFAULT_ICON,
  iconFor,
  resolveAppearance,
  suggestIcon,
} from '../../../../utils/biAppearance';
import { ReportCard } from '../../BI/ReportCard';
import { apllyToast } from '../../../Global/Toast2.0';
import { AppearancePicker } from '../../BI/AppearancePicker';
import { ModalComponent } from '..';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  group?: BiGroup;
  sample?: BiReport;
}

// Cor, ícone e ordem de um grupo (setor) no catálogo. Vale para todos os
// relatórios do grupo que não tiverem aparência própria.
export function ModalGroupBI({ isOpen, onClose, name, group, sample }: Props) {
  const queryClient = useQueryClient();
  const [color, setColor] = useState(BI_DEFAULT_COLOR);
  const [icon, setIcon] = useState(BI_DEFAULT_ICON);
  const [order, setOrder] = useState('0');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setColor(group?.color || BI_DEFAULT_COLOR);
    setIcon(group?.icon || suggestIcon(name) || BI_DEFAULT_ICON);
    setOrder(String(group?.display_order || 0));
  }, [isOpen, group, name]);

  // Ícone que combina com o nome do setor (ex.: Suinocultura → porco).
  const suggested = suggestIcon(name);
  const SuggestedIcon = iconFor(suggested);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    setSaving(true);
    try {
      await biService.upsertGroup({
        name,
        color,
        icon,
        display_order: Math.min(
          9999,
          Math.max(0, Math.trunc(Number(order) || 0)),
        ),
      });
      await queryClient.invalidateQueries(BI_GROUPS_KEY);
      apllyToast('success', 'Grupo atualizado.');
      onClose();
    } catch (err) {
      apllyToast(
        'error',
        biErrorMessage(err, 'Não foi possível salvar o grupo.'),
      );
    } finally {
      setSaving(false);
    }
  };

  // Prévia com um relatório do grupo, sem a aparência própria dele.
  const preview = {
    ...(sample || {}),
    id: 'preview',
    reportName: sample?.reportName || 'Nome do relatório',
    groupNameSamasc: name,
    summary: sample?.summary || sample?.description || 'Descrição curta',
    card_color: null,
    card_icon: null,
    isFavorite: false,
  } as BiReport;

  return (
    <ModalComponent
      title={`Personalizar grupo: ${name}`}
      isOpen={isOpen}
      onClose={onClose}
      sizeSpecifyc="2xl"
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <Box
        as="form"
        onSubmit={handleSubmit}
        display="flex"
        flexDirection="column"
        flex="1"
        minH="0"
      >
        <ModalBody>
          <Stack spacing="5">
            <Text fontSize="sm" color="gray.400">
              A cor e o ícone ajudam a reconhecer o setor no catálogo. Valem
              para todos os relatórios do grupo que não tiverem uma aparência
              própria.
            </Text>

            {suggested && suggested !== icon && (
              <Button
                size="sm"
                variant="outline"
                alignSelf="flex-start"
                leftIcon={<Icon as={SuggestedIcon} />}
                onClick={() => setIcon(suggested)}
              >
                Usar o ícone sugerido para {name}
              </Button>
            )}

            <AppearancePicker
              color={color}
              icon={icon}
              previewColor={color}
              onChange={value => {
                setColor(value.color || BI_DEFAULT_COLOR);
                setIcon(value.icon || BI_DEFAULT_ICON);
              }}
            />

            <FormControl maxW="220px">
              <FormLabel htmlFor="bi-group-order">Ordem no catálogo</FormLabel>
              <Input
                id="bi-group-order"
                type="number"
                min={0}
                max={9999}
                value={order}
                onChange={event => setOrder(event.target.value)}
              />
              <FormHelperText>Menor número aparece primeiro.</FormHelperText>
            </FormControl>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb="2">
                Prévia
              </Text>
              <Box maxW="320px" pointerEvents="none" aria-hidden>
                <ReportCard
                  report={preview}
                  appearance={resolveAppearance(null, { color, icon })}
                />
              </Box>
            </Box>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button colorScheme="blue" type="submit" isLoading={saving}>
            Salvar
          </Button>
        </ModalFooter>
      </Box>
    </ModalComponent>
  );
}
